import i18n from './i18n';

/**
 * The API returns codes, never sentences. This is where a code becomes something a
 * church administrator can read, in their language.
 */

export interface ApiErrorBody {
  error: {
    code: string;
    params?: Record<string, unknown>;
    details?: Record<string, string>;
  };
  requestId: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly params?: Record<string, unknown>;
  /** Field-keyed codes from a validation failure, for attaching to form fields. */
  readonly details?: Record<string, string>;
  /** Shown only in a copyable diagnostic on an unexpected failure, never in normal use. */
  readonly requestId?: string;

  constructor(status: number, body?: Partial<ApiErrorBody>) {
    super(body?.error?.code ?? 'NETWORK_ERROR');
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'NETWORK_ERROR';
    this.params = body?.error?.params;
    this.details = body?.error?.details;
    this.requestId = body?.requestId;

    /**
     * `message` is the sentence a person reads, resolved when it is read rather than
     * when the error was thrown, so an error held while the language changes still
     * renders in the language they are now looking at.
     *
     * Defined here rather than as a class getter because `Error`'s constructor
     * installs `message` as an own data property, which shadows anything on the
     * prototype — the accessor has to be installed on the instance to win.
     */
    Object.defineProperty(this, 'message', {
      get: () => translateErrorCode(this.code, this.params),
      configurable: true,
    });
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

export const translateErrorCode = (
  code: string,
  params?: Record<string, unknown>,
): string => {
  const key = `errors:${code}`;
  const translated = i18n.t(key, { ...params, defaultValue: '' });
  return translated || i18n.t('errors:UNEXPECTED');
};

/** Field errors, translated, ready to hand to a form. */
export const translateFieldErrors = (
  details: Record<string, string> | undefined,
): Record<string, string> => {
  if (!details) return {};

  return Object.fromEntries(
    Object.entries(details).map(([field, code]) => [
      field,
      i18n.t(`errors:field.${code}`, { defaultValue: i18n.t('errors:field.INVALID_VALUE') }),
    ]),
  );
};

/**
 * A module or plan refusal is not an error to apologise for — the client renders an
 * upgrade path for one and hides a navigation entry for the other, which is why the
 * API distinguishes them instead of returning a bare 403.
 */
export const isPlanRefusal = (error: unknown): boolean =>
  isApiError(error) && error.code === 'PLAN_UPGRADE_REQUIRED';

export const isModuleRefusal = (error: unknown): boolean =>
  isApiError(error) && error.code === 'MODULE_NOT_ENABLED';

export const isAuthenticationRefusal = (error: unknown): boolean =>
  isApiError(error) && error.status === 401;

/** Resolve an unknown thrown value to a user-facing sentence for toasts. */
export const resolveApiError = (
  error: unknown,
  tUnexpected: (key: string) => string = () => translateErrorCode('UNEXPECTED'),
): string => {
  if (isApiError(error)) return error.message;
  return tUnexpected('UNEXPECTED');
};
